import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "bc_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.sku === product.sku);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(9999, next[idx].qty + qty) };
        return next;
      }
      return [
        ...prev,
        {
          sku: product.sku,
          name: product.name,
          unit: product.unit,
          price: product.price,
          qty,
        },
      ];
    });
  };

  const updateQty = (sku, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.sku === sku ? { ...i, qty: Math.max(0, Math.min(9999, qty)) } : i))
        .filter((i) => i.qty > 0)
    );
  };

  const removeItem = (sku) => setItems((prev) => prev.filter((i) => i.sku !== sku));
  const clear = () => setItems([]);

  const { subtotal, count } = useMemo(() => {
    const s = items.reduce((acc, i) => acc + i.price * i.qty, 0);
    const c = items.reduce((acc, i) => acc + i.qty, 0);
    return { subtotal: Math.round(s * 100) / 100, count: c };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, subtotal, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
